/**
 * Unified Roles Table
 *
 * Single data table replacing both the expandable card list (Roles tab)
 * and the flat table (Reconciliation tab). Shows all Role Master entries
 * with F&O reconciliation columns inline.
 *
 * Features:
 * - Row click → detail drawer
 * - Context-sensitive action dropdown per row
 * - Row highlighting for title mismatch (amber) and grade mismatch (red)
 * - Suggestion display for unlinked entries
 */

import React from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import RoleRowActions, { type RoleRowActionsProps } from './RoleRowActions';
import type { UnifiedRoleMasterEntry, RoleMasterStatus } from '@/services/api/roleMasterService';

// ── Helpers ──────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<RoleMasterStatus, { label: string; color: string }> = {
  active: { label: 'Active', color: 'bg-emerald-100 text-emerald-700' },
  deprecated: { label: 'Deprecated', color: 'bg-gray-100 text-gray-500' },
  draft: { label: 'Draft', color: 'bg-amber-100 text-amber-700' },
};

const LINK_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  linked: { label: 'Linked', color: 'bg-emerald-100 text-emerald-700' },
  unlinked: { label: 'Unlinked', color: 'bg-gray-100 text-gray-500' },
  mismatch: { label: 'Mismatch', color: 'bg-amber-100 text-amber-700' },
};

// Sage → F&O ACCSITitleCode sync indicator (column badge)
const SAGE_SYNC_CONFIG: Record<string, { dot: string; label: string; tone: string }> = {
  in_sync:     { dot: 'bg-emerald-500', label: 'Sage code in sync with F&O',          tone: 'text-emerald-600' },
  out_of_sync: { dot: 'bg-red-500',     label: 'Sage code not in sync with F&O',      tone: 'text-red-600' },
  no_link:     { dot: 'bg-gray-300',    label: 'Sage code set — no linked F&O Title', tone: 'text-gray-500' },
  unknown:     { dot: 'bg-gray-200 border border-gray-300', label: 'F&O state unknown (service unreachable)', tone: 'text-gray-400' },
};

function truncate(s: string | undefined | null, max: number): string {
  if (!s) return '';
  return s.length > max ? s.slice(0, max) + '...' : s;
}

// ── Component ────────────────────────────────────────────────────────────

export interface UnifiedRolesTableProps {
  entries: UnifiedRoleMasterEntry[];
  onRowClick: (entry: UnifiedRoleMasterEntry) => void;
  // Delegate all row actions to parent (keeps this component presentational)
  actionHandlers: Omit<RoleRowActionsProps, 'entry'>;
  /** Clicking a sage-sync dot navigates to the Sage Sync panel focused on that row. */
  onOpenSageSync?: (entry: UnifiedRoleMasterEntry) => void;
}

const UnifiedRolesTable: React.FC<UnifiedRolesTableProps> = ({
  entries,
  onRowClick,
  actionHandlers,
  onOpenSageSync,
}) => {
  if (entries.length === 0) {
    return (
      <div className="py-12 text-center text-gray-400">
        No roles match the current filters.
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50/80">
            <TableHead className="w-[80px] text-xs font-medium">Code</TableHead>
            <TableHead className="min-w-[200px] text-xs font-medium">Title</TableHead>
            <TableHead className="text-xs font-medium">Function</TableHead>
            <TableHead className="text-xs font-medium">Department</TableHead>
            <TableHead className="w-[70px] text-xs font-medium">Grade</TableHead>
            <TableHead className="min-w-[180px] text-xs font-medium">F&O Job</TableHead>
            <TableHead className="w-[90px] text-xs font-medium">Link</TableHead>
            <TableHead className="w-[70px] text-xs font-medium text-center" title="Sage → F&O ACCSITitleCode sync state">Sage</TableHead>
            <TableHead className="w-[50px] text-xs font-medium text-center">Pos</TableHead>
            <TableHead className="w-[80px] text-xs font-medium">Status</TableHead>
            <TableHead className="w-[40px]" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map((entry) => {
            const statusCfg = STATUS_CONFIG[entry.status] || STATUS_CONFIG.active;
            const linkCfg = LINK_STATUS_CONFIG[entry.link_status] || LINK_STATUS_CONFIG.unlinked;
            const isMismatch = entry.link_status === 'mismatch';
            const isUnlinked = entry.link_status === 'unlinked';
            const topSuggestion = entry.suggestions?.[0];

            return (
              <TableRow
                key={entry.id}
                className={cn(
                  'cursor-pointer hover:bg-gray-50 transition-colors',
                  isMismatch && 'bg-amber-50/50',
                  entry.grade_mismatch && 'bg-red-50/30',
                  // Light amber for "RM has grade, F&O has none" — visually
                  // distinct from the red of a true mismatch.
                  entry.grade_missing && !entry.grade_mismatch && 'bg-amber-50/30',
                )}
                onClick={() => onRowClick(entry)}
              >
                {/* Code */}
                <TableCell className="font-mono text-xs text-gray-400 py-2">
                  {entry.role_code}
                </TableCell>

                {/* Title */}
                <TableCell className="py-2">
                  <span className="text-sm font-medium text-gray-900" title={entry.title}>
                    {truncate(entry.title, 45)}
                  </span>
                </TableCell>

                {/* Function */}
                <TableCell className="text-sm text-gray-600 py-2">
                  {truncate(entry.functional_area, 25)}
                </TableCell>

                {/* Department */}
                <TableCell className="text-sm text-gray-600 py-2">
                  {truncate(entry.department, 25)}
                </TableCell>

                {/* Grade (with mismatch + missing indicators) */}
                <TableCell className="py-2">
                  {entry.grade_level ? (
                    entry.grade_mismatch ? (
                      <span className="text-xs font-medium text-red-600" title={`RM: ${entry.grade_level}, F&O: ${entry.fo_compensation_level}`}>
                        {entry.grade_level} <span className="text-red-400">&#8800;</span> {entry.fo_compensation_level}
                      </span>
                    ) : entry.grade_missing ? (
                      <span
                        className="text-xs font-medium text-amber-700"
                        title="F&O JobCompensations is empty for this Job — push from Reconciliation tab to populate."
                      >
                        {entry.grade_level} <span className="text-amber-500 text-[10px] ml-0.5">(F&amp;O empty)</span>
                      </span>
                    ) : (
                      <span className="text-xs text-gray-600">{entry.grade_level}</span>
                    )
                  ) : entry.grade_only_in_fo && entry.fo_compensation_level ? (
                    <span
                      className="text-xs font-medium text-amber-700"
                      title="Set in F&O, not in Role Master. Use 'Adopt F&O grade' to copy it in."
                    >
                      {entry.fo_compensation_level} <span className="text-amber-500 text-[10px] ml-0.5">(F&amp;O)</span>
                    </span>
                  ) : (
                    <span className="text-xs text-gray-300">-</span>
                  )}
                </TableCell>

                {/* F&O Job */}
                <TableCell className="py-2">
                  {entry.fo_job_id ? (
                    <span className="text-xs font-mono text-gray-700" title={entry.fo_job_id}>
                      {truncate(entry.fo_job_id, 30)}
                    </span>
                  ) : topSuggestion ? (
                    <span className="text-xs italic text-gray-400" title={`Suggestion: ${topSuggestion.job_id} (${Math.round(topSuggestion.confidence * 100)}%)`}>
                      {truncate(topSuggestion.job_id, 25)}
                      <Badge variant="outline" className={cn(
                        'ml-1 text-[10px] px-1 py-0',
                        topSuggestion.confidence >= 0.85 ? 'text-emerald-600 border-emerald-300' :
                        topSuggestion.confidence >= 0.65 ? 'text-amber-600 border-amber-300' :
                        'text-red-600 border-red-300',
                      )}>
                        {Math.round(topSuggestion.confidence * 100)}%
                      </Badge>
                    </span>
                  ) : (
                    <span className="text-xs text-gray-300">-</span>
                  )}
                </TableCell>

                {/* Link Status */}
                <TableCell className="py-2">
                  <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0', linkCfg.color)}>
                    {linkCfg.label}
                  </Badge>
                </TableCell>

                {/* Sage → F&O sync state */}
                <TableCell className="py-2 text-center" onClick={(e) => e.stopPropagation()}>
                  {(() => {
                    const sage = entry.sage_title_code?.trim();
                    if (!sage) {
                      // No sage code at all — show em-dash (nothing to sync)
                      return <span className="text-xs text-gray-300">—</span>;
                    }
                    const state = entry.sage_code_sync_status || 'unknown';
                    const cfg = SAGE_SYNC_CONFIG[state] || SAGE_SYNC_CONFIG.unknown;
                    const tooltip = `${cfg.label}${sage ? ` · Code: ${sage}` : ''}`;
                    const dot = (
                      <span className="inline-flex items-center gap-1.5">
                        <span className={cn('inline-block h-2 w-2 rounded-full', cfg.dot)} />
                        <span className={cn('font-mono text-[10px]', cfg.tone)}>{sage}</span>
                      </span>
                    );
                    if (onOpenSageSync) {
                      return (
                        <button
                          type="button"
                          onClick={() => onOpenSageSync(entry)}
                          title={tooltip}
                          className="hover:opacity-80 transition-opacity"
                        >
                          {dot}
                        </button>
                      );
                    }
                    return <span title={tooltip}>{dot}</span>;
                  })()}
                </TableCell>

                {/* Positions count */}
                <TableCell className="text-center py-2">
                  {entry.linked_positions_count > 0 ? (
                    <span className="text-xs font-medium text-gray-700">{entry.linked_positions_count}</span>
                  ) : (
                    <span className="text-xs text-gray-300">0</span>
                  )}
                </TableCell>

                {/* Status */}
                <TableCell className="py-2">
                  <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0', statusCfg.color)}>
                    {statusCfg.label}
                  </Badge>
                </TableCell>

                {/* Actions */}
                <TableCell className="py-2" onClick={(e) => e.stopPropagation()}>
                  <RoleRowActions entry={entry} {...actionHandlers} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default UnifiedRolesTable;
